// src/hooks/useBabylonScene.js

import { useEffect, useRef } from 'react';
import {
  Engine,
  Scene,
  SceneLoader,
  Vector3,
  Color3,
  Color4,
  ArcRotateCamera,
  HemisphericLight,
  MeshBuilder,
  Ray,
  VertexBuffer,
  AbstractMesh,
  DynamicTexture,
  StandardMaterial,
  TransformNode
} from '@babylonjs/core';

import '@babylonjs/loaders/glTF';
import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture';

import { log, warn, error } from '../utils/loggingUtils';
import {
  availableMaterials,
  applyFloorMaterial,
  applyMaterialToMesh,
  MeshGroups
} from '../utils/materialUtils';

// Show axes for debugging
function showAxes(scene, size = 5) {
  const axisX = MeshBuilder.CreateLines(
    "axisX",
    { points: [new Vector3(0, 0, 0), new Vector3(size, 0, 0)] },
    scene
  );
  axisX.color = new Color3(1, 0, 0); // Red

  const axisY = MeshBuilder.CreateLines(
    "axisY",
    { points: [new Vector3(0, 0, 0), new Vector3(0, size, 0)] },
    scene
  );
  axisY.color = new Color3(0, 1, 0); // Green

  const axisZ = MeshBuilder.CreateLines(
    "axisZ",
    { points: [new Vector3(0, 0, 0), new Vector3(0, 0, size)] },
    scene
  );
  axisZ.color = new Color3(0, 0, 1); // Blue
}

// Show grid with labels for debugging
function showGridWithLabels(scene, size = 5) {
  const lines = [];
  for (let x = -size; x <= size; x++) {
    lines.push([new Vector3(x, 0, -size), new Vector3(x, 0, size)]);
  }
  for (let z = -size; z <= size; z++) {
    lines.push([new Vector3(-size, 0, z), new Vector3(size, 0, z)]);
  }
  const grid = MeshBuilder.CreateLineSystem("debugGridWithLabels", { lines }, scene);
  grid.color = new Color3(0.8, 0.8, 0.8);

  // Label intersections (optional)
  for (let x = -size; x <= size; x++) {
    for (let z = -size; z <= size; z++) {
      createTextLabel(scene, `(${x}, ${z})`, new Vector3(x, 0.01, z));
    }
  }
}

function createTextLabel(scene, text, position) {
  const planeSize = 0.5;
  const plane = MeshBuilder.CreatePlane(`label-${text}`, { size: planeSize }, scene);
  plane.position = position.clone();
  plane.billboardMode = AbstractMesh.BILLBOARDMODE_ALL;

  const dynamicTex = new DynamicTexture(`dt-${text}`, { width: 256, height: 256 }, scene);
  dynamicTex.hasAlpha = true;
  dynamicTex.drawText(text, null, 130, "bold 24px Arial", "white", "transparent", true);

  const mat = new StandardMaterial(`mat-${text}`, scene);
  mat.diffuseTexture = dynamicTex;
  mat.emissiveColor = new Color3(1, 1, 1);
  mat.backFaceCulling = false;

  plane.material = mat;
}

// ------------------------------
// WALLS / OBJECTS
// ------------------------------
export const useBabylonScene = ({
  canvasRef,
  floorType,
  floorMeshRef,
  setWalls,
  setObjects,
  setDoors,
  setWindows,
  setHoveredMeshInfo,
  setSelectedMeshInfo,
  setTooltipPos,
  sceneRef,
  engineRef
}) => {

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      warn('Canvas ref is not assigned. Cannot initialize BabylonJS engine.');
      return;
    }

    console.log('Initializing Babylon Engine and Scene...');
    const engine = new Engine(canvas, true, { preserveDrawingBuffer: true, stencil: true });
    engineRef.current = engine;

    const scene = new Scene(engine);
    sceneRef.current = scene;
    scene.useRightHandedSystem = true;
    scene.clearColor = new Color4(0.8, 0.8, 0.8, 1);

    console.log('Setting up camera...');
    const camera = new ArcRotateCamera(
      'camera',
      Math.PI / 2,
      Math.PI / 4,
      10,
      new Vector3(0, 1, 0),
      scene
    );
    camera.attachControl(canvas, true);

    console.log('Adding hemispheric light...');
    const light = new HemisphericLight('hemiLight', new Vector3(0, 1, 0), scene);
    light.intensity = 0.9;

    scene.createDefaultEnvironment({ createGround: false, createSkybox: false });

    try {
      console.log('Loading environment texture...');
      const envTexture = CubeTexture.CreateFromPrefilteredData(
        "https://assets.babylonjs.com/environments/environmentSpecular.env",
        scene
      );
      scene.environmentTexture = envTexture;
      console.log('Environment texture loaded successfully.');
    } catch (envErr) {
      error('Failed to load environment texture:', envErr);
    }

    // Show debug axes and grid
    showAxes(scene, 2);
    showGridWithLabels(scene, 5);

    const handleResize = () => { engine.resize(); };
    window.addEventListener('resize', handleResize);

    SceneLoader.OnPluginActivatedObservable.add((plugin) => {
      if (plugin.onMaterialLoadedObservable) {
        plugin.onMaterialLoadedObservable.add((mat) => {
          console.log(`Material Loaded: ${mat.name}`);
        });
      }
    });

    const modelUrl = 'https://yuxbcsdfnhttlrmzsqvz.supabase.co/storage/v1/object/public/products/bathroom.glb';
    console.log(`Starting to load main model from: ${modelUrl}`);
    let tempWalls = [];
    let originalWallMaterials = new Map();

    SceneLoader.Append('', modelUrl, scene,
      () => {
        console.log('Main model loading success callback triggered.');
        try {
          console.log('Processing loaded meshes...');
          const tempObjects = [];
          const tempDoors = [];
          const tempWindows = [];

          if (scene.meshes.length === 0) {
            warn('No meshes were loaded from the model. Check if the GLB file contains any meshes.');
          } else {
            console.log(`Number of meshes loaded: ${scene.meshes.length}`);
          }

          scene.meshes.forEach((mesh) => {
            const nameLower = mesh.name.toLowerCase();
            console.log(`Processing mesh: ${mesh.name}`);

            if (nameLower.includes('floor')) {
              floorMeshRef.current = mesh;
              const uvData = mesh.getVerticesData(VertexBuffer.UVKind);
              if (!uvData || uvData.length === 0) {
                assignUVs(mesh);
              }
              applyFloorMaterial(mesh, scene, floorType, log, warn);

            } else if (nameLower.includes('wall')) {
              mesh.group = MeshGroups.WALL;
              tempWalls.push(mesh);

              const uvData = mesh.getVerticesData(VertexBuffer.UVKind);
              if (!uvData || uvData.length === 0) {
                assignUVs(mesh, 'wall');
              }

              const defaultWallMaterial = availableMaterials.find(mat => mat.group === MeshGroups.WALL);
              if (defaultWallMaterial) {
                applyMaterialToMesh(mesh, scene, defaultWallMaterial, log, warn);
              }

            } else if (nameLower.includes('sink') ||
                       nameLower.includes('toilet') ||
                       nameLower.includes('storage')) {
              mesh.group = MeshGroups.OBJECT;
              tempObjects.push(mesh);
              console.log(`Object mesh detected: ${mesh.name}`);

            } else if (nameLower.includes('door')) {
              mesh.group = MeshGroups.DOOR;
              tempDoors.push(mesh);
              const defaultDoorMaterial = availableMaterials.find(mat => mat.group === MeshGroups.DOOR);
              if (defaultDoorMaterial) {
                applyMaterialToMesh(mesh, scene, defaultDoorMaterial, log, warn);
              }
              mesh.isVisible = false;
              mesh.currentMaterial = 'Hide';

            } else if (nameLower.includes('window')) {
              mesh.group = MeshGroups.WINDOW;
              tempWindows.push(mesh);
              const defaultWindowMaterial = availableMaterials.find(mat => mat.group === MeshGroups.WINDOW);
              if (defaultWindowMaterial) {
                applyMaterialToMesh(mesh, scene, defaultWindowMaterial, log, warn);
              }
              mesh.isVisible = false;
              mesh.currentMaterial = 'Hide';

            } else {
              mesh.group = MeshGroups.OBJECT;
            }
          });

          setWalls(tempWalls);
          setObjects(tempObjects);
          setDoors(tempDoors);
          setWindows(tempWindows);

          // Store original wall materials for later (when toggling visibility, etc.)
          tempWalls.forEach((wall) => {
            originalWallMaterials.set(wall, {
              material: wall.material,
              visibility: wall.visibility,
              isVisible: wall.isVisible
            });
          });

          // For optional "Hide the nearest wall" logic
          let centerOfRoom = new Vector3(0, 0, 0);
          if (floorMeshRef.current) {
            centerOfRoom = floorMeshRef.current.getBoundingInfo().boundingBox.centerWorld.clone();
          }

          const updateWallVisibility = () => {
            if (!scene.activeCamera) return;
            const cameraPos = scene.activeCamera.position;
            const direction = centerOfRoom.subtract(cameraPos).normalize();
            const distance = Vector3.Distance(cameraPos, centerOfRoom) + 1;
            const ray = new Ray(cameraPos, direction, distance);

            // Reset walls
            tempWalls.forEach((wall) => {
              const orig = originalWallMaterials.get(wall);
              if (orig) {
                wall.material = orig.material;
                wall.visibility = orig.visibility;
                wall.isVisible = orig.isVisible;
              }
            });

            const pickInfo = scene.pickWithRay(ray, (m) => m && m.group === MeshGroups.WALL);
            if (pickInfo && pickInfo.hit && pickInfo.pickedMesh) {
              pickInfo.pickedMesh.isVisible = false;
            }
          };

          if (scene.activeCamera) {
            scene.activeCamera.onViewMatrixChangedObservable.add(updateWallVisibility);
          }

          engine.runRenderLoop(() => {
            try {
              scene.render();
            } catch (e) {
              error('Rendering Error:', e);
            }
          });

          console.log('Main model load complete.');
        } catch (err) {
          error('Error in onSuccess callback:', err);
        }
      },
      undefined,
      (loaderScene, message, ex) => {
        error('Error loading model:', message, ex);
      }
    );

    // ---------- Example layout data with sink only ----------
    const layoutData = {
      "layout_id": 111,
      "placements": [
        {
          "name": "sink",
          "center": [
            -0.28,  // X
            -1.41   // Z
          ],
          "rotationFromXAxis": -134.10 + 180,
          "width": 0.7281789,
          "depth": 0.53999,
          "height": 0.5883292,
          // The sink should be this far above the floor's max Y
          "heightAboveFloor": 1.5
        }
      ]
    };

    layoutData.placements.forEach((pl) => {
      const nameLower = pl.name.toLowerCase();
      const glbFileName = `${nameLower}.glb`;
      loadAdditionalObject(glbFileName, pl);
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      engine.dispose();
    };
  }, [
    canvasRef,
    floorType,
    applyFloorMaterial,
    applyMaterialToMesh,
    setWalls,
    setObjects,
    setDoors,
    setWindows,
    setHoveredMeshInfo,
    setSelectedMeshInfo,
    setTooltipPos,
    sceneRef,
    engineRef
  ]);

  // A map from model filename to its URL
  const modelUrlMap = {
    'sideboard_8.glb': 'https://yuxbcsdfnhttlrmzsqvz.supabase.co/storage/v1/object/public/products/sideboard_8.glb?t=2024-12-18T04%3A06%3A24.808Z',
    'toilet.glb': 'https://yuxbcsdfnhttlrmzsqvz.supabase.co/storage/v1/object/public/products/Toilet.glb?t=2024-12-26T03%3A24%3A13.526Zb',
    'sink.glb': 'https://yuxbcsdfnhttlrmzsqvz.supabase.co/storage/v1/object/public/products/Sink.glb?t=2024-12-26T03%3A24%3A06.976Zb',
    'bathtub.glb': 'https://yuxbcsdfnhttlrmzsqvz.supabase.co/storage/v1/object/public/products/Bathtub.glb?t=2024-12-26T03%3A24%3A35.833Z',
    'door.glb': 'https://yuxbcsdfnhttlrmzsqvz.supabase.co/storage/v1/object/public/products/Door.glb'
  };

  // Load an additional object (sink in this case) and place it
  const loadAdditionalObject = (modelFileName, placement = null) => {
    console.log(`Attempting to load additional object: ${modelFileName}`);
    const scene = sceneRef.current;
    if (!scene) {
      warn('No scene available to load additional object into.');
      return;
    }

    // Get the floor's bounding info so we can compute floorMaxY
    let floorMaxY = 0;
    if (floorMeshRef.current) {
      const floorBI = floorMeshRef.current.getBoundingInfo();
      floorMaxY = floorBI.boundingBox.maximumWorld.y;
    }

    const modelUrlToLoad = modelUrlMap[modelFileName];
    if (!modelUrlToLoad) {
      warn(`No URL mapped for ${modelFileName}. Cannot load object.`);
      return;
    }
    console.log(`URL for additional object: ${modelUrlToLoad}`);

    SceneLoader.ImportMesh(
      null,
      '',
      modelUrlToLoad,
      scene,
      (meshes) => {
        console.log(`Additional object loaded: ${modelFileName}. Processing meshes...`);
        if (meshes.length === 0) {
          warn(`No meshes found in model ${modelFileName}.`);
          return;
        }

        // rootMesh is often no geometry; child meshes hold the real geometry
        const rootMesh = meshes[0];

        // Parent all sub-meshes to rootMesh
        meshes.forEach((m) => {
          if (m !== rootMesh) {
            m.setParent(rootMesh);
          }
        });

        // 1) Create a TransformNode that will act as our "proxy pivot"
        const pivotNode = new TransformNode(`pivotNode_${modelFileName}`, scene);

        // 2) Parent the rootMesh to that pivot
        rootMesh.setParent(pivotNode);

        // 3) Manually compute an aggregate bounding box across all child meshes
        const childMeshes = rootMesh.getChildMeshes();
        const { min, max } = getAggregatedBoundingBox(childMeshes);

        console.log('Aggregated bounding box:', { min, max });

        // 4) From the aggregated min/max, compute top-surface center in WORLD space
        const topSurfaceCenterWorld = new Vector3(
          (min.x + max.x) * 0.5,
          max.y, // top surface Y
          (min.z + max.z) * 0.5
        );
        console.log('topSurfaceCenterWorld:', topSurfaceCenterWorld);

        // 5) Convert that world point to local space of `rootMesh`
        rootMesh.computeWorldMatrix(true);
        const worldMatrixInv = rootMesh.getWorldMatrix().invert();
        const topSurfaceCenterLocal = Vector3.TransformCoordinates(
          topSurfaceCenterWorld,
          worldMatrixInv
        );
        console.log('topSurfaceCenterLocal:', topSurfaceCenterLocal);

        // 6) Shift the mesh so the bounding box’s top surface center is at (0,0,0) local
        rootMesh.position.subtractInPlace(topSurfaceCenterLocal);

        // 7) Position and rotate the pivot node
        if (placement) {
          // The pivot node's XZ = placement.center
          pivotNode.position.x = placement.center[0];
          pivotNode.position.z = placement.center[1];

          // (NEW) Place it so the bounding box top is floorMaxY + heightAboveFloor
          // i.e., the top surface center of the sink is at floorMaxY + yOffset
          const yOffset = placement.heightAboveFloor || 0;
          pivotNode.position.y = floorMaxY + yOffset;

          // Rotation around Y axis (in degrees)
          if (placement.rotationFromXAxis) {
            const rotationRad = placement.rotationFromXAxis * (Math.PI / 180);
            pivotNode.rotation.y = rotationRad;
          }
        }

        // Red debug sphere at pivot node
        const debugSphereRed = MeshBuilder.CreateSphere(
          `debugSphereRed_${modelFileName}`,
          { diameter: 0.05 },
          scene
        );
        debugSphereRed.material = new StandardMaterial(`debugSphereMatRed_${modelFileName}`, scene);
        debugSphereRed.material.diffuseColor = new Color3(1, 0, 0); 
        debugSphereRed.setParent(pivotNode);
        debugSphereRed.position.set(0, 0, 0);

        // Blue debug sphere showing top-surface center in the mesh’s local space
        const debugSphereBlue = MeshBuilder.CreateSphere(
          `debugSphereBlue_${modelFileName}`,
          { diameter: 0.05 },
          scene
        );
        debugSphereBlue.material = new StandardMaterial(`debugSphereMatBlue_${modelFileName}`, scene);
        debugSphereBlue.material.diffuseColor = new Color3(0, 0, 1); 
        debugSphereBlue.setParent(rootMesh);
        debugSphereBlue.position.copyFrom(topSurfaceCenterLocal);

        // Add to scene objects
        rootMesh.group = MeshGroups.OBJECT;
        setObjects((prev) => [...prev, rootMesh]);

        console.log(
          `Pivot-based object ${modelFileName} placed. ` +
          `Top-surface pivot at Y = ${pivotNode.position.y.toFixed(3)}`
        );
      },
      null,
      (scene, msg, exc) => {
        error(`Error loading additional object: ${modelFileName}`, msg, exc);
      }
    );
  };

  /**
   * Utility function to manually compute the bounding box
   * across multiple child meshes that contain geometry.
   * 
   * @param {AbstractMesh[]} childMeshes 
   * @returns {{min: Vector3, max: Vector3}}
   */
  function getAggregatedBoundingBox(childMeshes) {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let minZ = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let maxZ = Number.NEGATIVE_INFINITY;

    childMeshes.forEach((mesh) => {
      // Only consider geometry-containing meshes
      if (mesh.getTotalVertices() > 0) {
        mesh.computeWorldMatrix(true);
        mesh.refreshBoundingInfo();
        const binfo = mesh.getBoundingInfo();
        const bmin = binfo.boundingBox.minimumWorld;
        const bmax = binfo.boundingBox.maximumWorld;

        if (bmin.x < minX) minX = bmin.x;
        if (bmin.y < minY) minY = bmin.y;
        if (bmin.z < minZ) minZ = bmin.z;

        if (bmax.x > maxX) maxX = bmax.x;
        if (bmax.y > maxY) maxY = bmax.y;
        if (bmax.z > maxZ) maxZ = bmax.z;
      }
    });

    const minVec = new Vector3(minX, minY, minZ);
    const maxVec = new Vector3(maxX, maxY, maxZ);
    return { min: minVec, max: maxVec };
  }

  return { loadAdditionalObject };
};

// Assign UVs if missing
function assignUVs(mesh, meshType = 'floor') {
  const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
  if (!positions) return;
  const boundingInfo = mesh.getBoundingInfo();
  const min = boundingInfo.minimum;
  const max = boundingInfo.maximum;
  const size = { x: max.x - min.x, y: max.y - min.y, z: max.z - min.z };

  const uv = [];
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i];
    const y = positions[i + 1];
    const z = positions[i + 2];
    let u, v;
    if (meshType === 'wall') {
      // For walls, map (z,y) -> (u,v)
      u = (z - min.z) / size.z;
      v = (y - min.y) / size.y;
    } else {
      // For floors, map (x,z) -> (u,v)
      u = (x - min.x) / size.x;
      v = (z - min.z) / size.z;
    }
    uv.push(u, v);
  }
  mesh.setVerticesData(VertexBuffer.UVKind, uv, true);
  console.log(`UV assigned for ${mesh.name}`);
}
